import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { FormControlLabel, Checkbox, Card, Divider, ListItem, ListItemText, Icon } from "@mui/material";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

function MultiCheckboxField({ formRef, draftIndex, displayName, fieldRef, options }) {
  const [selected, setSelected] = useState([]);

  const handleChange = (value) => {
    setSelected((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const [hasSavedValue, setHasSavedValue] = useState(false);
  const [draftValue, setDraftValue] = useState(null);

  useEffect(() => {
    if (!formRef) return;

    const saved = localStorage.getItem(`draft_${formRef}`);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const val = parsed?.[draftIndex].values?.[fieldRef];

      if (val !== undefined && val !== null && val !== "") {
        setDraftValue(val);
        setHasSavedValue(true);
      }
    } catch (e) {
      console.error("Draft parse error", e);
    }
  }, [formRef, fieldRef]);

  return (
    <MDBox>
      {hasSavedValue ? (
        <MDBox>
          <ListItem divider>
            <MDBox mr={1}>
              <Icon color="success">check_circle</Icon>
            </MDBox>
            <ListItemText
              primary={
                <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                  {displayName}
                </MDTypography>
              }
              secondary={
                <MDTypography variant="body2" fontSize={13}>
                  {draftValue}
                </MDTypography>
              }
            />
            <MDButton
              size="small"
              color="warning"
              variant="outlined"
              iconOnly
              onClick={() => {
                try {
                  const key = `draft_${formRef}`;
                  const saved = localStorage.getItem(key);
                  if (!saved) return;

                  const parsed = JSON.parse(saved);

                  if (parsed?.values && fieldRef in parsed.values) {
                    delete parsed.values[fieldRef];
                  }

                  // persist updated draft
                  localStorage.setItem(key, JSON.stringify(parsed));

                  // update UI state
                  setHasSavedValue(false);
                  setDraftValue(null);
                } catch (e) {
                  console.error("Failed to clear draft field", e);
                }
              }}
            >
              <Icon>edit</Icon>
            </MDButton>
          </ListItem>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={draftValue}
          />
        </MDBox>
      ) : (
        <MDBox>
          <MDBox mb={1}>
            <MDTypography variant="h6" fontSize={14} fontWeight="light">
              {displayName}
            </MDTypography>
          </MDBox>

          <MDBox mb={1}>
            <Divider />
          </MDBox>

          {options?.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={selected.includes(opt.value)}
                  onChange={() => handleChange(opt.value)}
                />
              }
              label={opt.name}
            />
          ))}

          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={JSON.stringify(selected)}
          />
        </MDBox>
      )}
    </MDBox>
  );
}

// Typechecking props
MultiCheckboxField.propTypes = {
  formRef: PropTypes.string.isRequired,
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
};

MultiCheckboxField.defaultProps = {
  options: [],
};

export default MultiCheckboxField;
